import React, { useState } from 'react';
import './SegmentBuilder.scss';

const SegmentBuilder = ({ onClose, onSave, editingSegment = null }) => {
  const [segmentData, setSegmentData] = useState({
    name: editingSegment?.name || '',
    description: editingSegment?.description || '',
    conditions: editingSegment?.conditions || [
      {
        id: Date.now(),
        field: '',
        operator: '',
        value: '',
        type: 'and'
      }
    ]
  });

  const [previewCount, setPreviewCount] = useState(0);

  // Available fields for segmentation
  const segmentFields = [
    {
      key: 'application_status',
      label: 'Application Status',
      type: 'select',
      options: ['New', 'Screening', 'Interview Scheduled', 'Interview Completed', 'Offer Extended', 'Hired', 'Rejected']
    },
    {
      key: 'application_date',
      label: 'Application Date',
      type: 'date',
      operators: ['is', 'is_after', 'is_before', 'is_between']
    },
    {
      key: 'last_activity',
      label: 'Last Activity',
      type: 'date',
      operators: ['is', 'is_after', 'is_before', 'is_between']
    },
    {
      key: 'position_applied',
      label: 'Position Applied',
      type: 'text',
      operators: ['is', 'is_not', 'contains', 'not_contains']
    },
    {
      key: 'experience_level',
      label: 'Experience Level',
      type: 'select',
      options: ['Entry Level', 'Mid Level', 'Senior Level', 'Executive']
    },
    {
      key: 'location',
      label: 'Location',
      type: 'text',
      operators: ['is', 'is_not', 'contains', 'not_contains']
    },
    {
      key: 'source',
      label: 'Application Source',
      type: 'select',
      options: ['Website', 'LinkedIn', 'Indeed', 'Referral', 'Career Fair', 'Other']
    },
    {
      key: 'email_engagement',
      label: 'Email Engagement',
      type: 'select',
      options: ['High', 'Medium', 'Low', 'None']
    },
    {
      key: 'interview_score',
      label: 'Interview Score',
      type: 'number',
      operators: ['equals', 'greater_than', 'less_than', 'between']
    },
    {
      key: 'skills',
      label: 'Skills',
      type: 'text',
      operators: ['contains', 'not_contains']
    },
    {
      key: 'education_level',
      label: 'Education Level',
      type: 'select',
      options: ['High School', 'Associate', 'Bachelor', 'Master', 'PhD', 'Other']
    },
    {
      key: 'years_experience',
      label: 'Years of Experience',
      type: 'number',
      operators: ['equals', 'greater_than', 'less_than', 'between']
    }
  ];

  // Available operators based on field type
  const getOperators = (fieldType) => {
    const operators = {
      text: [
        { key: 'is', label: 'is' },
        { key: 'is_not', label: 'is not' },
        { key: 'contains', label: 'contains' },
        { key: 'not_contains', label: 'does not contain' }
      ],
      select: [
        { key: 'is', label: 'is' },
        { key: 'is_not', label: 'is not' }
      ],
      number: [
        { key: 'equals', label: 'equals' },
        { key: 'greater_than', label: 'greater than' },
        { key: 'less_than', label: 'less than' },
        { key: 'between', label: 'between' }
      ],
      date: [
        { key: 'is', label: 'is' },
        { key: 'is_after', label: 'is after' },
        { key: 'is_before', label: 'is before' },
        { key: 'is_between', label: 'is between' },
        { key: 'in_last', label: 'in the last' },
        { key: 'not_in_last', label: 'not in the last' }
      ]
    };
    return operators[fieldType] || operators.text;
  };

  const handleSave = () => {
    if (!segmentData.name.trim()) {
      alert('Please enter a segment name');
      return;
    }

    // Filter out incomplete conditions
    const validConditions = segmentData.conditions.filter(condition => 
      condition.field && condition.operator && condition.value
    );

    if (validConditions.length === 0) {
      alert('Please add at least one complete condition');
      return;
    }

    onSave({
      ...segmentData,
      conditions: validConditions,
      estimatedCount: previewCount
    });
  };

  const addCondition = () => {
    setSegmentData(prev => ({
      ...prev,
      conditions: [
        ...prev.conditions,
        {
          id: Date.now(),
          field: '',
          operator: '',
          value: '',
          type: 'and'
        }
      ]
    }));
  };

  const removeCondition = (conditionId) => {
    setSegmentData(prev => ({
      ...prev,
      conditions: prev.conditions.filter(c => c.id !== conditionId)
    }));
  };

  const updateCondition = (conditionId, updates) => {
    setSegmentData(prev => ({
      ...prev,
      conditions: prev.conditions.map(condition =>
        condition.id === conditionId
          ? { ...condition, ...updates }
          : condition
      )
    }));
  };

  const simulatePreview = () => {
    // Simulate segment preview - in real app, this would call API
    const baseCount = 1234;
    const conditionCount = segmentData.conditions.filter(c => c.field && c.operator).length;
    const estimated = Math.max(10, Math.floor(baseCount * Math.pow(0.7, conditionCount)));
    setPreviewCount(estimated);
  };

  React.useEffect(() => {
    const timer = setTimeout(simulatePreview, 500);
    return () => clearTimeout(timer);
  }, [segmentData.conditions]);

  const renderConditionValue = (condition, index) => {
    const field = segmentFields.find(f => f.key === condition.field);
    if (!field) return null;

    const commonProps = {
      value: condition.value,
      onChange: (e) => updateCondition(condition.id, { value: e.target.value }),
      className: "condition-input"
    };

    switch (field.type) {
      case 'select':
        return (
          <select {...commonProps}>
            <option value="">Select value...</option>
            {field.options.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        );
      
      case 'date':
        if (condition.operator === 'in_last' || condition.operator === 'not_in_last') {
          return (
            <div className="date-range-input">
              <input
                type="number"
                value={condition.value}
                onChange={(e) => updateCondition(condition.id, { value: e.target.value })}
                placeholder="Number"
                className="condition-input date-number"
                min="1"
              />
              <select
                value={condition.unit || 'days'}
                onChange={(e) => updateCondition(condition.id, { unit: e.target.value })}
                className="condition-input date-unit"
              >
                <option value="days">days</option>
                <option value="weeks">weeks</option>
                <option value="months">months</option>
              </select>
            </div>
          );
        }
        return <input type="date" {...commonProps} />;
      
      case 'number':
        if (condition.operator === 'between') {
          return (
            <div className="number-range-input">
              <input
                type="number"
                value={condition.value}
                onChange={(e) => updateCondition(condition.id, { value: e.target.value })}
                placeholder="Min"
                className="condition-input range-min"
              />
              <span className="range-separator">to</span>
              <input
                type="number"
                value={condition.maxValue || ''}
                onChange={(e) => updateCondition(condition.id, { maxValue: e.target.value })}
                placeholder="Max"
                className="condition-input range-max"
              />
            </div>
          );
        }
        return <input type="number" {...commonProps} />;
      
      default:
        return <input type="text" {...commonProps} placeholder="Enter value..." />;
    }
  };

  return (
    <div className="segment-builder-modal">
      <div className="segment-builder-container">
        {/* Header */}
        <div className="builder-header">
          <div className="header-content">
            <h2>🎯 {editingSegment ? 'Edit Segment' : 'Create New Segment'}</h2>
            <p>Define your target audience with custom criteria</p>
          </div>
          <div className="header-actions">
            <button className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleSave}>
              {editingSegment ? 'Update Segment' : 'Save Segment'}
            </button>
          </div>
        </div>

        <div className="builder-content">
          <div className="builder-main">
            {/* Segment Info */}
            <div className="segment-info-section">
              <h3>Segment Information</h3>
              
              <div className="form-group">
                <label>Segment Name *</label>
                <input
                  type="text"
                  value={segmentData.name}
                  onChange={(e) => setSegmentData(prev => ({
                    ...prev,
                    name: e.target.value
                  }))}
                  placeholder="e.g., High-potential candidates"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={segmentData.description}
                  onChange={(e) => setSegmentData(prev => ({
                    ...prev,
                    description: e.target.value
                  }))}
                  placeholder="Describe this segment..."
                  className="form-textarea"
                  rows="3"
                />
              </div>
            </div>

            {/* Conditions Builder */}
            <div className="conditions-section">
              <div className="section-header">
                <h3>Conditions</h3>
                <p>Define the criteria candidates must meet to be included in this segment</p>
              </div>

              <div className="conditions-list">
                {segmentData.conditions.map((condition, index) => (
                  <div key={condition.id} className="condition-row">
                    {index > 0 && (
                      <div className="condition-logic">
                        <select
                          value={condition.type}
                          onChange={(e) => updateCondition(condition.id, { type: e.target.value })}
                          className="logic-select"
                        >
                          <option value="and">AND</option>
                          <option value="or">OR</option>
                        </select>
                      </div>
                    )}
                    
                    <div className="condition-content">
                      <div className="condition-field">
                        <select
                          value={condition.field}
                          onChange={(e) => updateCondition(condition.id, { 
                            field: e.target.value, 
                            operator: '', 
                            value: '' 
                          })}
                          className="field-select"
                        >
                          <option value="">Select field...</option>
                          {segmentFields.map(field => (
                            <option key={field.key} value={field.key}>
                              {field.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      {condition.field && (
                        <>
                          <div className="condition-operator">
                            <select
                              value={condition.operator}
                              onChange={(e) => updateCondition(condition.id, { 
                                operator: e.target.value, 
                                value: '' 
                              })}
                              className="operator-select"
                            >
                              <option value="">Select condition...</option>
                              {getOperators(
                                segmentFields.find(f => f.key === condition.field)?.type || 'text'
                              ).map(op => (
                                <option key={op.key} value={op.key}>
                                  {op.label}
                                </option>
                              ))}
                            </select>
                          </div>

                          {condition.operator && (
                            <div className="condition-value">
                              {renderConditionValue(condition, index)}
                            </div>
                          )}
                        </>
                      )}

                      <div className="condition-actions">
                        {segmentData.conditions.length > 1 && (
                          <button
                            className="remove-condition-btn"
                            onClick={() => removeCondition(condition.id)}
                            title="Remove condition"
                          >
                            🗑️
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                <button className="add-condition-btn" onClick={addCondition}>
                  + Add Condition
                </button>
              </div>
            </div>
          </div>

          {/* Preview Panel */}
          <div className="preview-panel">
            <div className="preview-header">
              <h4>📊 Segment Preview</h4>
              <div className="preview-count">
                <span className="count-number">{previewCount.toLocaleString()}</span>
                <span className="count-label">estimated candidates</span>
              </div>
            </div>

            <div className="preview-content">
              <div className="segment-summary">
                <h5>Conditions Summary</h5>
                {segmentData.conditions.length === 0 ? (
                  <p className="empty-conditions">No conditions defined</p>
                ) : (
                  <div className="conditions-preview">
                    {segmentData.conditions.map((condition, index) => {
                      const field = segmentFields.find(f => f.key === condition.field);
                      const operator = getOperators(field?.type || 'text').find(o => o.key === condition.operator);
                      
                      if (!field || !operator || !condition.value) {
                        return (
                          <div key={condition.id} className="condition-preview incomplete">
                            <span>Incomplete condition</span>
                          </div>
                        );
                      }

                      return (
                        <div key={condition.id} className="condition-preview">
                          {index > 0 && (
                            <span className="logic-operator">
                              {condition.type.toUpperCase()}
                            </span>
                          )}
                          <div className="condition-text">
                            <strong>{field.label}</strong> {operator.label} <em>{condition.value}</em>
                            {condition.maxValue && <em> to {condition.maxValue}</em>}
                            {condition.unit && <em> {condition.unit}</em>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="segment-stats">
                <h5>Segment Statistics</h5>
                <div className="stats-grid">
                  <div className="stat-item">
                    <span className="stat-label">Total Pool</span>
                    <span className="stat-value">1,234</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Match Rate</span>
                    <span className="stat-value">
                      {((previewCount / 1234) * 100).toFixed(1)}%
                    </span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Estimated Reach</span>
                    <span className="stat-value">{previewCount.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              <div className="segment-examples">
                <h5>Example Candidates</h5>
                <div className="examples-list">
                  {previewCount > 0 ? (
                    <>
                      <div className="example-candidate">
                        <div className="candidate-name">Sarah Johnson</div>
                        <div className="candidate-details">Software Developer • Applied 5 days ago</div>
                      </div>
                      <div className="example-candidate">
                        <div className="candidate-name">Mike Chen</div>
                        <div className="candidate-details">Product Manager • Interview scheduled</div>
                      </div>
                      <div className="example-candidate">
                        <div className="candidate-name">Emma Davis</div>
                        <div className="candidate-details">UX Designer • High engagement</div>
                      </div>
                      <div className="examples-more">
                        + {Math.max(0, previewCount - 3).toLocaleString()} more candidates
                      </div>
                    </>
                  ) : (
                    <p className="no-matches">No candidates match the current criteria</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SegmentBuilder;